import { useEffect, useState } from "react";
import {
	Box,
	Button,
	Chip,
	IconButton,
	InputAdornment,
	Paper,
	TextField,
	Tooltip,
	Typography,
	useTheme,
} from "@mui/material";
import { Bolt, CompareArrows, Search, Update } from "@mui/icons-material";
import dayjs from "dayjs";
import useStyles from "./styles";
import statusVersao from "../statusVersao";
import api from "../../../services/api";
import DatagridComponent from "../../../components/datagridComponent";
import ModalConfirmacao from "../modalConfirmacao";
import ModalDivergencia from "../modalDivergencia";
import ModalForcarAtualizacao from "../modalForcarAtualizacao";
import ModalAtualizarScripts from "../modalAtualizarScripts";
import ModalForcarScripts from "../modalForcarScripts";
import alvoId from "../alvoId";
import rotuloAlvo from "../rotuloAlvo";

// Acima deste limite de clientes, o componente ganha busca interna sem tocar no resto da tela
const LIMITE_SEM_BUSCA = 15;

export default function ListaClientesDoItem({
	item,
	onTemplateAtualizado,
	tipo = "template",
	scripts,
	saudeColeta,
}) {
	const [busca, setBusca] = useState("");
	const [modalConfirmacaoAberto, setModalConfirmacaoAberto] = useState(false);
	const [modalAtualizarAberto, setModalAtualizarAberto] = useState(false);
	const [ultimasAtualizacoes, setUltimasAtualizacoes] = useState(new Map());
	const [clienteEmInspecao, setClienteEmInspecao] = useState(null);
	const [clienteParaForcar, setClienteParaForcar] = useState(null);
	const theme = useTheme();
	const styles = useStyles(theme);
	const cores = statusVersao(theme);

	// Busca o historico de atualizacao desse template - uma unica chamada por expansao de linha,
	// nao uma por cliente
	useEffect(() => {
		buscarUltimasAtualizacoes();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [item?.chave]);

	// GET /versionamento/{templates|scripts}/historico, reduzido ao evento mais recente por alvo (cliente + proxy)
	async function buscarUltimasAtualizacoes() {
		if (!item) return;
		if (tipo === "template" && !item?.chave) return;
		try {
			// Scripts gravam a chave no evento quando a acao e por script, e null quando e cliente inteiro.
			// A coluna "Resultado" desta linha (um script) considera os dois: o evento dessa chave e o de
			// cliente inteiro (null), o mais recente vence. Por isso scripts busca sem filtro de chave e
			// filtra em memoria; templates continuam filtrando por chave na propria query
			const params =
				tipo === "script"
					? { evento: "atualizacao", limit: 100 }
					: { chave: item.chave, evento: "atualizacao", limit: 100 };
			const response = await api.get(`/versionamento/${tipo}s/historico`, { params });
			// Reduz para o evento mais recente por alvo (a API ja devolve ordenado por iniciadoEm desc) -
			// templates nao tem proxy, entao o alvo e so o cliente
			const maisRecentePorCliente = new Map();
			for (const evento of response?.data || []) {
				// Pra scripts, so conta o evento desta chave ou o de cliente inteiro (chave null)
				if (tipo === "script" && evento.chave != null && evento.chave !== item?.chave) continue;
				if (!maisRecentePorCliente.has(alvoId(evento))) {
					maisRecentePorCliente.set(alvoId(evento), evento);
				}
			}
			setUltimasAtualizacoes(maisRecentePorCliente);
		} catch {
			// falha silenciosa - coluna so fica vazia, nao quebra a tela
			setUltimasAtualizacoes(new Map());
		}
	}

	// Chamado quando o ModalConfirmacao termina uma atualizacao - recarrega o grid do pai
	// e refaz a busca de historico pra essa mesma linha
	function handleConcluido() {
		onTemplateAtualizado?.();
		buscarUltimasAtualizacoes();
	}

	// Par fundo/texto do status do evento de atualizacao - mesmos tokens de chip.background/colorText,
	// sem token novo pra abortado_divergencia (mesmo par default de "Ausente no cliente"/"Sem versão")
	function coresEvento(status) {
		if (status === "sucesso") {
			return {
				background: theme.palette.chip.background.success,
				color: theme.palette.chip.colorText.success,
			};
		}
		if (status === "falha") {
			return {
				background: theme.palette.chip.background.critical,
				color: theme.palette.chip.colorText.critical,
			};
		}
		return {
			background: theme.palette.chip.background.default,
			color: theme.palette.chip.colorText.default,
		};
	}

	// Celula da coluna "Resultado da ultima atualizacao" - "-" quando nao ha evento pra esse cliente.
	// Recebe o evento ja resolvido (evita buscar de novo em ultimasAtualizacoes) e o cliente, pra tambem
	// decidir e disparar o botao "Forcar mesmo assim" com o mesmo evento usado no chip
	function renderResultadoAtualizacao(evento, cliente) {
		if (!evento) return "—";
		const cor = coresEvento(evento?.status);
		// "Forcar mesmo assim" e sempre por cliente e so depois de uma falha - vale pra templates e scripts.
		// Pra scripts, forca so este script (item.chave) neste cliente; o onClick abaixo carrega a chave
		const podeForcar =
			evento?.status === "falha" || evento?.status === "abortado_divergencia";
		return (
			<Box sx={styles.resultadoAtualizacao}>
				<Box sx={styles.linhaChip}>
					<Chip
						size="small"
						label={evento?.status}
						sx={{
							...styles.chip,
							backgroundColor: cor.background,
							color: cor.color,
						}}
					/>
					{podeForcar && (
						<Tooltip title="Forçar mesmo assim">
							<IconButton
								size="small"
								onClick={() =>
									setClienteParaForcar({
										clienteId: cliente?.clienteId,
										proxy: cliente?.proxy,
										chave: item?.chave,
										nomeCliente: cliente?.nome,
										motivo: evento?.motivo,
									})
								}
							>
								<Bolt fontSize="small" />
							</IconButton>
						</Tooltip>
					)}
				</Box>
				{evento?.status !== "sucesso" && evento?.motivo && (
					<Typography variant="caption" sx={styles.motivoEvento}>
						{evento.motivo}
					</Typography>
				)}
			</Box>
		);
	}

	// Nao renderiza nada enquanto nenhuma linha do grid estiver selecionada
	if (!item) return null;

	const ehScript = tipo === "script";

	const clientes = item?.clientes || [];
	const precisaBusca = clientes.length > LIMITE_SEM_BUSCA;

	// Filtragem interna por nome do cliente (e do proxy, quando houver), so quando a base ultrapassa o limite
	const clientesFiltrados = precisaBusca
		? clientes.filter((cliente) =>
				rotuloAlvo(cliente)?.toLowerCase()?.includes(busca.toLowerCase()),
			)
		: clientes;

	// Elegiveis para "Forcar atualizacao": Desatualizado (versao velha) e Ausente no cliente (nunca
	// instalado) - os dois passam pelo mesmo atualizarTemplateCliente no backend, que so cria quando
	// falta e so atualiza quando ja existe
	const clientesDesatualizados = clientes.filter(
		(cliente) => cliente?.status === "Desatualizado" || cliente?.status === "Ausente no cliente",
	);

	// Linhas do grid ja com o evento de atualizacao resolvido, disponivel em row.resultadoEvento
	// pro renderCell da coluna de resultado nao precisar fazer lookup dentro da celula
	const rows = clientesFiltrados.map((cliente) => ({
		...cliente,
		id: alvoId(cliente),
		rotulo: rotuloAlvo(cliente),
		resultadoEvento: ultimasAtualizacoes.get(alvoId(cliente)),
	}));

	const columns = [
		{ field: "rotulo", headerName: "Cliente", flex: 1, minWidth: 160 },
		{
			field: "versaoCliente",
			headerName: "Versão instalada",
			width: 150,
			renderCell: (params) => params.row?.versaoCliente || "—",
		},
		{
			field: "status",
			headerName: "Status",
			width: 170,
			renderCell: (params) => (
				<Box sx={styles.statusCelula}>
					<Chip
						size="small"
						label={params.row?.status}
						sx={{
							...styles.chip,
							backgroundColor: cores[params.row?.status]?.background,
							color: cores[params.row?.status]?.color,
						}}
					/>
					{!ehScript && params.row?.status === "Desatualizado" && (
						<Tooltip title="Ver divergência">
							<IconButton
								size="small"
								onClick={() =>
									setClienteEmInspecao({
										clienteId: params.row?.clienteId,
										chave: item?.chave,
										nomeCliente: params.row?.nome,
									})
								}
							>
								<CompareArrows fontSize="small" />
							</IconButton>
						</Tooltip>
					)}
				</Box>
			),
		},
		{
			field: "ultimaColeta",
			headerName: "Última coleta",
			width: 170,
			renderCell: (params) =>
				params.row?.ultimaColeta
					? dayjs(params.row.ultimaColeta).format("DD/MM/YYYY HH:mm")
					: "—",
		},
		// Coluna "Resultado da ultima atualizacao" existe pros dois tipos - pra scripts, resolve pro
		// ultimo evento de execucao daquele cliente (a unidade de execucao e o cliente, nao o script)
		{
			field: "resultado",
			headerName: "Resultado da última atualização",
			flex: 1,
			minWidth: 220,
			renderCell: (params) =>
				renderResultadoAtualizacao(params.row?.resultadoEvento, params.row),
		},
	];

	return (
		<Box sx={styles.container}>
			<Box sx={styles.cabecalho}>
				<Box sx={styles.tituloGrupo}>
					<Typography variant="subtitle1" sx={styles.titulo}>
						Clientes — {item?.nome || item?.chave}
					</Typography>
					{!ehScript && (item?.desatualizado > 0 || item?.ausente > 0) && (
						<Button
							size="small"
							variant="contained"
							startIcon={<Update />}
							onClick={() => setModalConfirmacaoAberto(true)}
						>
							Forçar atualização
						</Button>
					)}
					{ehScript && (item?.desatualizado > 0 || item?.ausente > 0) && (
						<Button
							size="small"
							variant="contained"
							startIcon={<Update />}
							onClick={() => setModalAtualizarAberto(true)}
						>
							Atualizar este script nos clientes
						</Button>
					)}
				</Box>
				{precisaBusca && (
					<TextField
						size="small"
						placeholder="Buscar cliente..."
						value={busca}
						onChange={(event) => setBusca(event.target.value)}
						sx={styles.buscaField}
						InputProps={{
							startAdornment: (
								<InputAdornment position="start">
									<Search fontSize="small" />
								</InputAdornment>
							),
						}}
					/>
				)}
			</Box>
			{/* Paper garante o fundo/contraste corretos do grid nos dois modos, mesmo padrao ja usado em abaTemplates/Clientes/Staff/Home */}
			<Paper sx={styles.paper} elevation={theme.palette.mode === "dark" ? 0 : 3}>
				{/* Altura fixa so quando o grid tem mais de 4 linhas - mesmo limiar do autoHeight interno
				do DatagridComponent, senao o Box força altura mesmo quando o grid so tem 1-2 clientes */}
				{rows.length > 4 ? (
					<Box sx={styles.gridContainer}>
						<DatagridComponent rows={rows} columns={columns} />
					</Box>
				) : (
					<DatagridComponent rows={rows} columns={columns} />
				)}
			</Paper>
			{clientesFiltrados?.length === 0 && (
				<Typography variant="body2" sx={styles.semResultado}>
					Nenhum cliente encontrado para "{busca}".
				</Typography>
			)}
			{/* Nenhum dos 3 modais de acao de template faz sentido pra scripts - a unidade de acao de
			scripts e o cliente inteiro, com o ModalAtualizarScripts proprio (Fase 2 do Plano 2) */}
			{!ehScript && (
				<>
					<ModalConfirmacao
						open={modalConfirmacaoAberto}
						onClose={() => setModalConfirmacaoAberto(false)}
						item={item}
						clientesDesatualizados={clientesDesatualizados}
						onConcluido={handleConcluido}
					/>
					<ModalDivergencia
						open={Boolean(clienteEmInspecao)}
						onClose={() => setClienteEmInspecao(null)}
						clienteId={clienteEmInspecao?.clienteId}
						chave={clienteEmInspecao?.chave}
						nomeCliente={clienteEmInspecao?.nomeCliente}
					/>
					<ModalForcarAtualizacao
						open={Boolean(clienteParaForcar)}
						onClose={() => setClienteParaForcar(null)}
						clienteId={clienteParaForcar?.clienteId}
						chave={clienteParaForcar?.chave}
						nomeCliente={clienteParaForcar?.nomeCliente}
						onConcluido={handleConcluido}
					/>
				</>
			)}
			{ehScript && (
				<>
					<ModalAtualizarScripts
						open={modalAtualizarAberto}
						onClose={() => setModalAtualizarAberto(false)}
						scripts={scripts}
						chave={item?.chave}
						saudeColeta={saudeColeta}
						onConcluido={handleConcluido}
					/>
					<ModalForcarScripts
						open={Boolean(clienteParaForcar)}
						onClose={() => setClienteParaForcar(null)}
						clienteId={clienteParaForcar?.clienteId}
						proxy={clienteParaForcar?.proxy}
						nomeCliente={clienteParaForcar?.nomeCliente}
						motivoFalha={clienteParaForcar?.motivo}
						chaveSolicitada={clienteParaForcar?.chave}
						scripts={scripts}
						onConcluido={handleConcluido}
					/>
				</>
			)}
		</Box>
	);
}
